import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Grid,
  TextField,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip,
  TablePagination,
  useTheme,
  useMediaQuery,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
} from '@mui/material';
import {
  Visibility as ViewIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Delete as DeleteIcon,
  Warning as WarningIcon,
  MonetizationOn as MoneyIcon,
  History as HistoryIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { matkaAPI, marketAPI } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const MatkaBets = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [bets, setBets] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detailsDialog, setDetailsDialog] = useState({ open: false, bet: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, result: null });
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [stats, setStats] = useState({});
  const [tabValue, setTabValue] = useState(0); // 0 = Bets, 1 = Results
  
  const [filters, setFilters] = useState({
    marketId: '',
    date: '',
    status: '',
    betType: '',
    userId: '',
  });

  const gameTypeNames = {
    1: "SINGLE_DIGIT",
    2: "SINGLE_DIGIT_BULK", 
    3: "JODI_DIGIT",
    4: "JODI_DIGIT_BULK",
    5: "SINGLE_PANNA",
    6: "SINGLE_PANNA_BULK",
    7: "DOUBLE_PANNA", 
    8: "DOUBLE_PANNA_BULK",
    9: "TRIPLE_PANNA",
    10: "TRIPLE_PANNA_BULK",
    11: "HALF_SANGAM",
    12: "FULL_SANGAM",
    13: "RED_JODI"
  };

  const gameModeNames = {
    'open': 'Open',
    'close': 'Close',
    'both': 'Both',
    'odcp': 'ODCP',
    'cdop': 'CDOP'
  };

  useEffect(() => {
    fetchBets();
    fetchMarkets();
    if (user?.user_type === 'owner') {
      fetchResults();
    }
  }, [filters, page, rowsPerPage]);

  const fetchBets = async () => {
    try {
      setLoading(true);
      const params = {
        ...filters,
        limit: rowsPerPage,
        offset: page * rowsPerPage,
      };
      
      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await matkaAPI.getMatkaBets(params);
      if (response.data.success) {
        setBets(response.data.bets || []);
        setStats(response.data.stats || {});
      } else {
        toast.error('Failed to fetch bets');
      }
    } catch (err) {
      console.error('Error fetching bets:', err);
      toast.error(err.response?.data?.message || 'Failed to fetch bets');
    } finally {
      setLoading(false);
    }
  };

  const fetchMarkets = async () => {
    try {
      const response = await marketAPI.getMarkets();
      if (response.data.success) {
        setMarkets(response.data.markets || []);
      }
    } catch (err) {
      console.error('Error fetching markets:', err);
    }
  };

  const fetchResults = async () => {
    try {
      const params = {
        ...filters,
        limit: 50, // Show more results for deletion
      };
      
      Object.keys(params).forEach(key => {
        if (!params[key]) delete params[key];
      });

      const response = await matkaAPI.getMatkaResults(params);
      if (response.data.success) {
        setResults(response.data.results || []);
      }
    } catch (err) {
      console.error('Error fetching results:', err);
    }
  };

  const handleViewDetails = async (bet) => {
    try {
      const response = await matkaAPI.getMatkaBetById(bet.id);
      if (response.data.success) {
        setDetailsDialog({ open: true, bet: response.data.bet });
      } else {
        toast.error('Failed to fetch bet details');
      }
    } catch (err) {
      console.error('Error fetching bet details:', err);
      toast.error('Failed to fetch bet details');
    }
  };

  const handleDeleteResult = async () => {
    try {
      if (!deleteDialog.result) return;

      setLoading(true);
      const response = await matkaAPI.deleteMatkaResult(deleteDialog.result.id);
      
      if (response.data.success) {
        toast.success(
          `Result deleted successfully! ${response.data.affected_bets} bets reversed. ` +
          `Total amount reversed: ₹${response.data.reversed_amount.toFixed(2)}`
        );
        setDeleteDialog({ open: false, result: null });
        
        // Refresh data
        fetchBets();
        fetchResults();
      } else {
        toast.error(response.data.message || 'Failed to delete result');
      }
    } catch (err) {
      console.error('Error deleting result:', err);
      toast.error(err.response?.data?.message || 'Failed to delete result');
    } finally {
      setLoading(false);
    }
  };

  const getStatusChip = (status) => {
    const statusConfig = {
      pending: { color: 'warning', label: 'Pending' },
      win: { color: 'success', label: 'Win' },
      loss: { color: 'error', label: 'Loss' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    return (
      <Chip
        label={config.label}
        color={config.color}
        size="small"
        variant="outlined"
      />
    );
  };

  const getBetTypeChip = (betType) => {
    const colors = {
      1: 'primary', 2: 'primary', 3: 'secondary', 4: 'secondary',
      5: 'success', 6: 'success', 7: 'warning', 8: 'warning',
      9: 'error', 10: 'error', 11: 'info', 12: 'info', 13: 'default'
    };
    
    return (
      <Chip
        label={gameTypeNames[betType] || `Type ${betType}`}
        color={colors[betType] || 'default'}
        size="small"
        variant="filled"
        sx={{ fontSize: '0.7rem' }}
      />
    );
  };

  const formatCurrency = (amount) => {
    return `₹${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const clearFilters = () => {
    setFilters({
      marketId: '',
      date: '',
      status: '',
      betType: '',
      userId: '',
    });
    setPage(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <Box sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ mr: 2 }} />
            Matka Bets & Results
          </Typography>
          <Box display="flex" gap={2}>
            <Button
              variant="outlined"
              startIcon={<FilterIcon />}
              endIcon={filtersOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              Filters
            </Button>
            {user?.user_type === 'owner' && (
              <Button
                variant={tabValue === 0 ? "contained" : "outlined"}
                startIcon={<ReceiptIcon />}
                onClick={() => setTabValue(0)}
                sx={{ mr: 1 }}
              >
                Bets
              </Button>
            )}
            {user?.user_type === 'owner' && (
              <Button
                variant={tabValue === 1 ? "contained" : "outlined"}
                startIcon={<HistoryIcon />}
                onClick={() => setTabValue(1)}
                color="secondary"
              >
                Results
              </Button>
            )}
          </Box>
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ scale: 1.02 }}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
                color: 'white',
                height: '140px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <CardContent sx={{ width: '100%', '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>Total Bets</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
                        {stats.total || 0}
                      </Typography>
                    </Box>
                    <ReceiptIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ scale: 1.02 }}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', 
                color: 'white',
                height: '140px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <CardContent sx={{ width: '100%', '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>Pending</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '2rem' }}>
                        {stats.pending || 0}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ scale: 1.02 }}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', 
                color: 'white',
                height: '140px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <CardContent sx={{ width: '100%', '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
                    <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>Win Bets</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '2rem', lineHeight: 1 }}>
                        {stats.win || 0}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8, fontSize: '0.8rem', mt: 0.5 }}>
                        {formatCurrency(stats.total_win_amount)}
                      </Typography>
                    </Box>
                    <TrendingUpIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <motion.div whileHover={{ scale: 1.02 }}>
              <Card sx={{ 
                background: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', 
                color: 'white',
                height: '140px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <CardContent sx={{ width: '100%', '&:last-child': { pb: 2 } }}>
                  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ height: '100%' }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6" sx={{ mb: 1, fontSize: '1rem' }}>Total Amount</Typography>
                      <Typography variant="h4" sx={{ fontWeight: 'bold', fontSize: '1.4rem', wordBreak: 'break-word' }}>
                        {formatCurrency(stats.total_bet_amount)}
                      </Typography>
                    </Box>
                    <TrendingDownIcon sx={{ fontSize: 48, opacity: 0.8 }} />
                  </Box>
                </CardContent>
              </Card>
            </motion.div>
          </Grid>
        </Grid>

        {/* Filters */}
        <Collapse in={filtersOpen}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>Filter {tabValue === 0 ? 'Bets' : 'Results'}</Typography>
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Market"
                    select
                    value={filters.marketId}
                    onChange={(e) => setFilters({ ...filters, marketId: e.target.value })}
                    size="small"
                  >
                    <MenuItem value="">All Markets</MenuItem>
                    {markets.map((market) => (
                      <MenuItem key={market.id} value={market.id}>
                        {market.market_name}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} sm={6} md={2}>
                  <TextField
                    fullWidth
                    label="Date"
                    type="date"
                    value={filters.date}
                    onChange={(e) => setFilters({ ...filters, date: e.target.value })}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                  />
                </Grid>

                {tabValue === 0 && (
                  <>
                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        label="Status"
                        select
                        value={filters.status}
                        onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="">All Status</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="win">Win</MenuItem>
                        <MenuItem value="loss">Loss</MenuItem>
                      </TextField>
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        fullWidth
                        label="Game Type"
                        select
                        value={filters.betType}
                        onChange={(e) => setFilters({ ...filters, betType: e.target.value })}
                        size="small"
                      >
                        <MenuItem value="">All Types</MenuItem>
                        {Object.entries(gameTypeNames).map(([id, name]) => (
                          <MenuItem key={id} value={id}>{name}</MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </>
                )}

                {(user?.user_type === 'admin' || user?.user_type === 'owner') && tabValue === 0 && (
                  <Grid item xs={12} sm={6} md={2}>
                    <TextField
                      fullWidth
                      label="User ID"
                      value={filters.userId}
                      onChange={(e) => setFilters({ ...filters, userId: e.target.value })}
                      size="small"
                    />
                  </Grid>
                )}

                <Grid item xs={12} sm={6} md={2}>
                  <Button
                    fullWidth
                    variant="outlined"
                    onClick={clearFilters}
                    sx={{ height: '40px' }}
                  >
                    Clear All
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Collapse>

        {/* Bets Table */}
        {tabValue === 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>Matka Bets</Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader size={isMobile ? "small" : "medium"}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Bet ID</TableCell>
                      <TableCell>User</TableCell>
                      <TableCell>Market</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Number</TableCell>
                      <TableCell>Mode</TableCell>
                      <TableCell align="center">Amount</TableCell>
                      <TableCell align="center">Win Amount</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {bets.map((bet) => (
                      <TableRow key={bet.id} hover>
                        <TableCell>#{bet.id}</TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {bet.user?.username || `User ${bet.user_id}`}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {bet.market?.market_name || 'Unknown'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          {getBetTypeChip(bet.bet_type)}
                        </TableCell>
                        <TableCell>
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontFamily: 'monospace',
                              fontWeight: 'bold',
                              px: 1,
                              py: 0.5,
                              borderRadius: 1,
                              display: 'inline-block'
                            }}
                          >
                            {bet.number}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={gameModeNames[bet.game_mode] || bet.game_mode}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                            {formatCurrency(bet.points)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'bold', 
                              color: bet.win_amount > 0 ? 'success.main' : 'text.secondary' 
                            }}
                          >
                            {formatCurrency(bet.win_amount)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {getStatusChip(bet.status)}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(bet.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="View Details">
                            <IconButton
                              size="small"
                              onClick={() => handleViewDetails(bet)}
                              color="info"
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {bets.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={11} align="center">
                          <Typography color="textSecondary" sx={{ py: 4 }}>
                            No bets found for the selected criteria
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={stats.total || 0}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </CardContent>
          </Card>
        )}

        {/* Results Table (Only for Owner) */}
        {tabValue === 1 && user?.user_type === 'owner' && (
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                ⚠️ Matka Results - DELETE FUNCTIONALITY (Owner Only)
              </Typography>
              <Typography variant="body2" color="warning.main" sx={{ mb: 2, p: 1.5, bgcolor: 'rgba(255, 152, 0, 0.1)', borderRadius: 1, border: '1px solid', borderColor: 'warning.main' }}>
                <strong>Warning:</strong> Deleting results will reverse all winning bets and balances. 
                Users with insufficient balance will have negative balances that get deducted on next recharge.
              </Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                <Table stickyHeader size="medium">
                  <TableHead>
                    <TableRow>
                      <TableCell>Date</TableCell>
                      <TableCell>Market</TableCell>
                      <TableCell align="center">Open</TableCell>
                      <TableCell align="center">Close</TableCell>
                      <TableCell align="center">Jodi</TableCell>
                      <TableCell>Declared By</TableCell>
                      <TableCell>Time</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {results.map((result) => (
                      <TableRow key={result.id} hover>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                            {new Date(result.result_date).toLocaleDateString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {result.market?.market_name}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Chip 
                              label={result.open_panna || '-'}
                              color="primary"
                              size="small"
                              sx={{ fontFamily: 'monospace', mb: 0.5, display: 'block' }}
                            />
                            <Chip 
                              label={result.open_single !== null ? result.open_single : '-'}
                              color="secondary"
                              size="small"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Chip 
                              label={result.close_panna || '-'}
                              color="primary"
                              size="small"
                              sx={{ fontFamily: 'monospace', mb: 0.5, display: 'block' }}
                            />
                            <Chip 
                              label={result.close_single !== null ? result.close_single : '-'}
                              color="secondary"
                              size="small"
                              sx={{ fontFamily: 'monospace' }}
                            />
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip 
                            label={result.jodi || '-'}
                            color="success"
                            size="small"
                            sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {result.declarer?.username || 'System'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(result.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Delete Result & Reverse Bets">
                            <IconButton
                              size="small"
                              onClick={() => setDeleteDialog({ open: true, result })}
                              color="error"
                              sx={{ ml: 1 }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                    
                    {results.length === 0 && !loading && (
                      <TableRow>
                        <TableCell colSpan={8} align="center">
                          <Typography color="textSecondary" sx={{ py: 4 }}>
                            No results found for the selected criteria
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        )}

        {/* Bet Details Dialog */}
        <Dialog 
          open={detailsDialog.open} 
          onClose={() => setDetailsDialog({ open: false, bet: null })}
          maxWidth="md" 
          fullWidth
        >
          <DialogTitle>Bet Details - #{detailsDialog.bet?.id}</DialogTitle>
          <DialogContent>
            {detailsDialog.bet && (
              <Grid container spacing={3} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">User</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {detailsDialog.bet.user?.username} (ID: {detailsDialog.bet.user_id})
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Market</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {detailsDialog.bet.market?.market_name}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Game Type</Typography>
                  <Box sx={{ mb: 2 }}>
                    {getBetTypeChip(detailsDialog.bet.bet_type)}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Game Mode</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {gameModeNames[detailsDialog.bet.game_mode] || detailsDialog.bet.game_mode}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Number/Combination</Typography>
                  <Typography 
                    variant="h6" 
                    sx={{ 
                      fontFamily: 'monospace',
                      fontWeight: 'bold',
                      bgcolor: 'primary.light',
                      px: 2,
                      py: 1,
                      borderRadius: 1,
                      display: 'inline-block',
                      mb: 2
                    }}
                  >
                    {detailsDialog.bet.number}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Status</Typography>
                  <Box sx={{ mb: 2 }}>
                    {getStatusChip(detailsDialog.bet.status)}
                  </Box>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Bet Amount</Typography>
                  <Typography variant="h6" color="primary.main" sx={{ mb: 2 }}>
                    {formatCurrency(detailsDialog.bet.points)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Win Amount</Typography>
                  <Typography 
                    variant="h6" 
                    color={detailsDialog.bet.win_amount > 0 ? "success.main" : "text.secondary"}
                    sx={{ mb: 2 }}
                  >
                    {formatCurrency(detailsDialog.bet.win_amount)}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Bet Date</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {detailsDialog.bet.bet_date}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">Created At</Typography>
                  <Typography variant="body1" sx={{ mb: 2 }}>
                    {formatDate(detailsDialog.bet.createdAt)}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDetailsDialog({ open: false, bet: null })}>
              Close
            </Button>
          </DialogActions>
        </Dialog>

        {/* Delete Result Dialog */}
        <Dialog 
          open={deleteDialog.open} 
          onClose={() => setDeleteDialog({ open: false, result: null })}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle sx={{ color: 'error.main' }}>
            <Box display="flex" alignItems="center">
              <WarningIcon sx={{ mr: 1 }} />
              Delete Matka Result
            </Box>
          </DialogTitle>
          <DialogContent>
            {deleteDialog.result && (
              <Box>
                <Box sx={{ mb: 3, p: 2, bgcolor: 'rgba(244, 67, 54, 0.1)', borderRadius: 1, border: '1px solid', borderColor: 'error.main' }}>
                  <Typography variant="h6" gutterBottom color="error.main">
                    ⚠️ Critical Action Warning
                  </Typography>
                  <Typography variant="body2" color="error.main">
                    This action will <strong>permanently delete</strong> the result and 
                    <strong> reverse all winning bets</strong>. This cannot be undone!
                  </Typography>
                </Box>

                <Typography variant="h6" gutterBottom>
                  Result to Delete:
                </Typography>
                
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <HistoryIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Date" 
                      secondary={new Date(deleteDialog.result.result_date).toLocaleDateString()}
                    />
                  </ListItem>
                  
                  <ListItem>
                    <ListItemIcon>
                      <ReceiptIcon color="primary" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Market" 
                      secondary={deleteDialog.result.market?.market_name}
                    />
                  </ListItem>

                  <ListItem>
                    <ListItemIcon>
                      <MoneyIcon color="success" />
                    </ListItemIcon>
                    <ListItemText 
                      primary="Results" 
                      secondary={`Open: ${deleteDialog.result.open_panna || '-'} | Close: ${deleteDialog.result.close_panna || '-'} | Jodi: ${deleteDialog.result.jodi || '-'}`}
                    />
                  </ListItem>
                </List>

                <Divider sx={{ my: 2 }} />

                <Typography variant="body2" color="error">
                  <strong>What will happen:</strong>
                </Typography>
                <Typography variant="body2" component="ul" sx={{ mt: 1, pl: 2 }}>
                  <li>All winning bets will be reversed to "pending" status</li>
                  <li>Win amounts will be deducted from user wallets</li>
                  <li>Users with insufficient balance will have negative balance</li>
                  <li>Negative balances will be recovered on next recharge</li>
                  <li>All losing bets will also return to "pending" status</li>
                </Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button 
              onClick={() => setDeleteDialog({ open: false, result: null })}
              color="inherit"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleDeleteResult}
              color="error"
              variant="contained"
              disabled={loading}
              startIcon={<DeleteIcon />}
            >
              {loading ? 'Deleting...' : 'Delete & Reverse'}
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </motion.div>
  );
};

export default MatkaBets;
